import {
  format,
  startOfMonth,
  endOfMonth,
  endOfYear,
  startOfYear,
  startOfWeek,
  endOfDay,
  endOfWeek,
  startOfDay,
} from 'date-fns';

export function getStartDate(dateRange, { earliestEventDate } = {}) {
  const today = new Date();

  switch (dateRange) {
    case 'today':
      return format(startOfDay(new Date()), 'yyyy-MM-dd HH:mm:ss');
    case 'yesterday':
      return format(startOfDay(new Date(new Date().setDate(today.getDate() - 1))), 'yyyy-MM-dd HH:mm:ss');
    case 'thisWeek':
      return format(startOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd HH:mm:ss');
    case 'pastWeek':
      return format(
        startOfWeek(new Date(new Date().setDate(today.getDate() - 7)), { weekStartsOn: 1 }),
        'yyyy-MM-dd HH:mm:ss',
      );
    /*
     * N - 1, because today is one of the N days.
     *
     * These subtracted the full N and still ran to the end of today, so "Last 7 Days" returned
     * eight — 29 July through 5 August inclusive. The start was right for a different
     * convention, "the last seven complete days", and the end was later moved to today without
     * the total being counted again.
     *
     * Today is included deliberately, matching last12Months and yearToDate, and the charts
     * already mark an incomplete final bucket. The alternative — excluding today, as the ad
     * platforms do — would make these three presets disagree with every other one here.
     *
     * `Past Week` sat in the same dropdown returning exactly seven days, which is how the
     * discrepancy showed up at all.
     */
    case 'last7Days':
      return format(startOfDay(new Date(new Date().setDate(today.getDate() - 6))), 'yyyy-MM-dd HH:mm:ss');
    case 'last28Days':
      return format(startOfDay(new Date(new Date().setDate(today.getDate() - 27))), 'yyyy-MM-dd HH:mm:ss');
    case 'last90Days':
      return format(startOfDay(new Date(new Date().setDate(today.getDate() - 89))), 'yyyy-MM-dd HH:mm:ss');
    /*
     * Month-aligned, like last12Months and unlike the day-based presets.
     *
     * A cohort grid's rows are calendar periods, so a range beginning mid-month gives it a
     * first row covering part of a month while labelled like a whole one. `Last 90 Days` is not
     * wrong — it returns 90 days, as promised — but there was no way to ask for whole months
     * short of a year. These are that way.
     */
    case 'last3Months':
      return format(startOfMonth(new Date(new Date().setMonth(today.getMonth() - 3))), 'yyyy-MM-dd HH:mm:ss');
    case 'last6Months':
      return format(startOfMonth(new Date(new Date().setMonth(today.getMonth() - 6))), 'yyyy-MM-dd HH:mm:ss');
    case 'last12Months':
      return format(startOfMonth(new Date(new Date().setMonth(today.getMonth() - 12))), 'yyyy-MM-dd HH:mm:ss');
    case 'thisMonth':
      return format(startOfMonth(today), 'yyyy-MM-dd HH:mm:ss');
    case 'lastMonth':
      return format(startOfMonth(new Date(new Date().setMonth(today.getMonth() - 1))), 'yyyy-MM-dd HH:mm:ss');
    case 'thisYear':
      return format(startOfYear(today), 'yyyy-MM-dd HH:mm:ss');
    case 'yearToDate':
      return format(startOfYear(today), 'yyyy-MM-dd HH:mm:ss');
    case 'lastYear':
      return format(startOfYear(new Date(new Date().setFullYear(today.getFullYear() - 1))), 'yyyy-MM-dd HH:mm:ss');
    /*
     * The app's own first day, when it is known.
     *
     * This was `today - 5 years`: a rolling window wearing the name of an absolute one. For app
     * 1 it silently cut everything before August 2021 though the first transaction is from
     * April 2020, and the cut advanced by a day every day. A label that makes a promise has to
     * keep it.
     *
     * `earliestEventDate` comes from chart meta. The five-year window remains as the fallback
     * for callers that have no meta yet — wrong in the same old way, but no worse, and only
     * until the first meta response lands.
     */
    case 'allTime':
      return earliestEventDate
        ? format(startOfDay(new Date(`${earliestEventDate}T00:00:00`)), 'yyyy-MM-dd HH:mm:ss')
        : format(startOfDay(new Date(new Date().setFullYear(today.getFullYear() - 5))), 'yyyy-MM-dd HH:mm:ss');
    default:
      return format(startOfDay(new Date(new Date().setDate(today.getDate() - 89))), 'yyyy-MM-dd HH:mm:ss');
  }
}

export function getEndDate(dateRange) {
  const today = new Date();

  switch (dateRange) {
    case 'today':
      return format(endOfDay(new Date()), 'yyyy-MM-dd HH:mm:ss');
    case 'yesterday':
      return format(new Date(new Date().setDate(today.getDate() - 1)), 'yyyy-MM-dd HH:mm:ss');
    case 'thisWeek':
      return format(endOfDay(new Date()), 'yyyy-MM-dd HH:mm:ss');
    case 'pastWeek':
      return format(
        endOfWeek(new Date(new Date().setDate(today.getDate() - 7)), { weekStartsOn: 1 }),
        'yyyy-MM-dd HH:mm:ss',
      );
    case 'last7Days':
      return format(endOfDay(new Date()), 'yyyy-MM-dd HH:mm:ss');
    case 'last28Days':
      return format(endOfDay(new Date()), 'yyyy-MM-dd HH:mm:ss');
    case 'last90Days':
      return format(endOfDay(new Date()), 'yyyy-MM-dd HH:mm:ss');
    case 'last3Months':
    case 'last6Months':
    case 'last12Months':
      return format(new Date(), 'yyyy-MM-dd HH:mm:ss');
    case 'thisMonth':
      return format(endOfMonth(new Date()), 'yyyy-MM-dd HH:mm:ss');
    case 'lastMonth':
      return format(endOfMonth(new Date(new Date().setMonth(today.getMonth() - 1))), 'yyyy-MM-dd HH:mm:ss');
    case 'thisYear':
      // legacy alias — persisted selections now behave as Year to Date (Jan 1 → today)
      return format(endOfDay(today), 'yyyy-MM-dd HH:mm:ss');
    case 'yearToDate':
      return format(endOfDay(today), 'yyyy-MM-dd HH:mm:ss');
    case 'lastYear':
      return format(endOfYear(new Date(new Date().setFullYear(today.getFullYear() - 1))), 'yyyy-MM-dd HH:mm:ss');
    case 'allTime':
      return format(endOfDay(new Date()), 'yyyy-MM-dd HH:mm:ss');
    default:
      return format(endOfDay(new Date()), 'yyyy-MM-dd HH:mm:ss');
  }
}

export function getFilterDate(value, dateRange) {
  switch (dateRange) {
    case 'days':
      return {
        start: format(startOfDay(new Date(new Date().setDate(new Date().getDate() - value))), 'yyyy-MM-dd HH:mm:ss'),
        end: format(endOfDay(new Date(new Date().setDate(new Date().getDate() - value))), 'yyyy-MM-dd HH:mm:ss'),
      };
    case 'weeks':
      return {
        start: format(
          startOfWeek(new Date(new Date().setDate(new Date().getDate() - value * 7)), { weekStartsOn: 1 }),
          'yyyy-MM-dd HH:mm:ss',
        ),
        end: format(
          endOfWeek(new Date(new Date().setDate(new Date().getDate() - value * 7)), { weekStartsOn: 1 }),
          'yyyy-MM-dd HH:mm:ss',
        ),
      };
    case 'months':
      return {
        start: format(
          startOfMonth(new Date(new Date().setMonth(new Date().getMonth() - value))),
          'yyyy-MM-dd HH:mm:ss',
        ),
        end: format(endOfMonth(new Date(new Date().setMonth(new Date().getMonth() - value))), 'yyyy-MM-dd HH:mm:ss'),
      };
    case 'years':
      return {
        start: format(
          startOfYear(new Date(new Date().setFullYear(new Date().getFullYear() - value))),
          'yyyy-MM-dd HH:mm:ss',
        ),
        end: format(
          endOfYear(new Date(new Date().setFullYear(new Date().getFullYear() - value))),
          'yyyy-MM-dd HH:mm:ss',
        ),
      };
    default:
      return {
        start: format(startOfDay(new Date(new Date().setDate(new Date().getDate() - 90))), 'yyyy-MM-dd HH:mm:ss'),
        end: format(endOfDay(new Date()), 'yyyy-MM-dd HH:mm:ss'),
      };
  }
}
